/* eslint-disable max-lines */
import * as React from 'react'
import {useMemo} from 'react'
import {
  StateHandler,
  StateHandlerMap,
  StateUpdaters,
  withStateHandlers
} from 'recompose'
import {Lang} from 'yougile-lang'
import {ArrUtil, Updater, Util} from 'yougile-util'
import {
  createEmptyReportColumn,
  DataObjectId,
  defaultDataFields,
  getAllFields,
  identitySchedule,
  isEmptyReport,
  Report,
  ReportColumn,
  ReportSchedule
} from 'yougile-model'
import {Debouncer} from '@common/debouncer'
import {Button} from '@components/common/base/buttons'
import {showConfirm} from '@components/common/base/notifications'
import {LinkButton, LinkButtonSmall} from '@components/common/button/button'
import {Help} from '@components/common/help/help'
import {TextInput} from '@components/common/text-input/text-input'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {asyncGetAllReportTasks} from '@components/statistics/stats-reports/stats-reports-get-tasks'
import {
  useBoolean,
  useDebouncedValue,
  useReportTasksIds
} from '@containers/hooks'
import {Task} from '@extensions/task'
import {Sync} from '../../../client/sync'
import {getAllStickers} from '../../../extensions/sticker/sticker-data-extractor'
import {UserStickerState} from '../../../extensions/sticker/views-web/user/user-sticker-state'
import {HelpHint} from '../../common/help-hint/help-hint'
import {Popup} from '../../common/popup/popup'
import {MessageBox} from '../../message-box'
import {UserAvatar} from '../../user-avatar'
import AddIcon from './add.svg'
import {Filters} from './filters'
import {Schedule} from './schedule'
import {Sort} from './sort'
import {StatsReportsColumns} from './stats-reports-columns'

export interface StatsReportsEditProps {
  initialReport?: Report
  saveReport: (report: Report) => void
  deleteReport: () => Promise<boolean>
  close: () => void
  isStatReport?: boolean
  isAggregatedColumn?: boolean
  admins?: DataObjectId[]
  managers?: DataObjectId[]
  canEdit: boolean
}
interface State {
  currentReport: Report
}
interface Handlers extends StateHandlerMap<State> {
  changeReport: StateHandler<State>
}
const nameDebouncer = new Debouncer(50)

// eslint-disable-next-line max-lines-per-function
const StatsReportsEditBase = (
  props: State & Handlers & StatsReportsEditProps
) => {
  const {
    initialReport,
    currentReport,
    changeReport,
    saveReport,
    close,
    deleteReport,
    managers,
    admins,
    isStatReport = false,
    canEdit,
    isAggregatedColumn = false
  } = props
  const bem = Util.bemFrom('stats-reports-edit')
  const saveClick = () => {
    saveReport({
      ...currentReport,
      title: currentReport.title || Lang.msg.component.filters.withoutName
    })
    close()
  }
  const firstColumnIndex = 0
  const firstColumn = currentReport.columns[firstColumnIndex]

  const deleteColumn = (columnIndex: number) => {
    const newColumns = ArrUtil.deletedAtIndex(
      currentReport.columns,
      columnIndex
    )
    changeReport({
      ...currentReport,
      columns: newColumns
    })
  }
  const cloneColumn = (columnIndex: number) => {
    const newColumns = ArrUtil.insert(
      currentReport.columns,
      columnIndex + 1,
      currentReport.columns[columnIndex]
    )
    changeReport({
      ...currentReport,
      columns: newColumns
    })
  }
  const changeColumn = (columnIndex: number) => (newCol: ReportColumn) => {
    if (!canEdit) {
      MessageBox.warn(Lang.msg.report.editReportDisallowed)
      return
    }
    const newColumns = ArrUtil.changedAtIndex(
      currentReport.columns,
      newCol,
      columnIndex
    )
    changeReport({...currentReport, columns: newColumns})
  }

  const addNewColumnReport = () => {
    changeReport({
      ...currentReport,
      columns: [...currentReport.columns, createEmptyReportColumn()]
    })
  }

  const update =
    (columnIndex: number) =>
    <T extends keyof ReportColumn>(fieldName: T) =>
    (newVal: ReportColumn[T]) => {
      changeColumn(columnIndex)({
        ...currentReport.columns[columnIndex],
        [fieldName]: newVal
      })
    }
  const onChange = (value: string) => {
    if (!canEdit) {
      MessageBox.warn(Lang.msg.report.editReportDisallowed)
      return
    }
    nameDebouncer.runLatest(() => {
      changeReport({...currentReport, title: value})
    })
  }
  const deleteAllTasks = async () => {
    // todo: implement fast batch delete on server and remove this
    if (await showConfirm(Lang.msg.report.removeAllTasks)) {
      const tasks = await asyncGetAllReportTasks(
        Sync.getCurrentSandbox(),
        currentReport
      )
      Task.bunchDelete(
        box,
        tasks.map(t => t?.id)
      )
      close()
    }
  }
  const del = async (e: React.MouseEvent) => {
    if (e.shiftKey && e.altKey) {
      await deleteAllTasks()
      return
    }
    if (await deleteReport()) {
      close()
    }
  }

  const updateSchedule: Updater<ReportSchedule> = fn => () =>
    changeReport({
      ...currentReport,
      schedule: fn(identitySchedule(currentReport.schedule))
    })
  const title = isAggregatedColumn
    ? Lang.msg.report.columnEditTitle
    : initialReport?.title
      ? isStatReport
        ? Lang.msg.statsEditReport
        : Lang.msg.statsEditColumnReport + ` “${initialReport.title}”`
      : isStatReport
        ? Lang.msg.statsAddReport
        : Lang.msg.statsAddColumnReport

  const scheduleValid = useBoolean(true)

  const isEmailValid = (email: string) =>
    !currentReport.schedule?.enabled ||
    (!!email.trim() && Util.isEmail(email.trim()))

  const areAllEmailsValid = currentReport.schedule?.emails.every(isEmailValid)

  const footer = canEdit ? (
    <div className="flex items-center justify-center">
      <div className={bem('cancel')} onClick={close}>
        {Lang.msg.cancel}
      </div>
      <Button
        onClick={saveClick}
        disabled={
          currentReport.schedule?.enabled &&
          (!scheduleValid.value || !areAllEmailsValid)
        }
        className="ml-24"
      >
        {Lang.msg.save}
      </Button>
    </div>
  ) : null
  const choose = (chosen: DataObjectId[]) => {
    const newReport: Report = {
      ...currentReport,
      availableTo: chosen
    }
    changeReport(newReport)
  }

  const availableTo = currentReport.availableTo ?? admins
  const debouncedCurrentReport = useDebouncedValue(currentReport, 500)

  const {data: tasks} = useReportTasksIds({
    report: debouncedCurrentReport,
    shouldUpdateOnDataChange: false
  })
  const taskCount = useMemo(() => tasks?.length ?? 0, [tasks])
  const box = Sync.getCurrentSandbox()
  const stickers = getAllStickers(box, true)
  const allFields = getAllFields(stickers)
  const chosenFields =
    firstColumn.fields ?? defaultDataFields.map(f => f(Lang.msg).key)

  return (
    <Popup
      width={838}
      title={title}
      footer={footer}
      close={close}
      className={bem('popup')}
    >
      <WithPerfectScrollbar>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {isAggregatedColumn
              ? Lang.msg.report.columnName
              : isStatReport
                ? Lang.msg.statsReportName
                : Lang.msg.statsColumnReportName}
            :
            <TextInput
              className="ml-16 w-216"
              value={currentReport.title}
              autoFocus={true}
              onChange={onChange}
              placeholder={Lang.msg.report.namePlaceHolder}
              large={true}
            />
            {!isEmptyReport(debouncedCurrentReport) && (
              <div className="ml-24 text-14">
                {Lang.msg.component.filters.found}:{' '}
                <span className="font-semibold">
                  {Lang.msg.projectTasks(taskCount)}
                </span>
              </div>
            )}
          </div>
          {canEdit && !!initialReport?.title && (
            <LinkButton onClick={del}>
              {isAggregatedColumn
                ? Lang.msg.report.deleteColumn
                : isStatReport
                  ? Lang.msg.statsDeleteReport
                  : Lang.msg.statsDeleteColumnReport}
            </LinkButton>
          )}
        </div>
        <div className={bem('section')}>
          <div className={bem('sub-header', {filter: true})}>
            {Lang.msg.statsFilter}
          </div>
          {currentReport.columns.map((column, i) => (
            <div key={i} className="mb-24 ml-8">
              <Filters
                isAggregatedColumn={isAggregatedColumn}
                canEdit={canEdit}
                index={i}
                cloneColumn={() => cloneColumn(i)}
                deleteColumn={
                  currentReport.columns.length > 1
                    ? () => deleteColumn(i)
                    : undefined
                }
                currentColumn={column}
                changeColumn={changeColumn(i)}
              />
            </div>
          ))}
          {canEdit && !isAggregatedColumn && (
            <div className="flex items-center ml-12">
              <LinkButtonSmall className="mr-4" onClick={addNewColumnReport}>
                <AddIcon className="mr-2" />
                {Lang.msg.component.filters.addFilterGroup}
              </LinkButtonSmall>
              <div className="mr-8 text-third-old text-12">
                ({Lang.msg.component.filters.resultsCombined})
              </div>
              <Help name="filterGroup" />
            </div>
          )}
        </div>
        {!isAggregatedColumn && (
          <div className={bem('section')}>
            <div className={bem('sub-header', {sort: true})}>
              {Lang.msg.statsSort}
            </div>
            <Sort
              sort={firstColumn.sort}
              update={update(firstColumnIndex)('sort')}
            />
          </div>
        )}
        {isStatReport && (
          <div className={bem('section')}>
            <div className={bem('sub-header', {table: true})}>
              {Lang.msg.report.tableTitle}
            </div>
            <StatsReportsColumns
              chosen={chosenFields}
              allFields={allFields}
              update={update(firstColumnIndex)('fields')}
            />
          </div>
        )}
        {isStatReport && (
          <div className={bem('section')}>
            <div className={bem('sub-header', {share: true})}>
              {Lang.msg.report.availableForTitle}
            </div>
            <div className={bem('available-for')}>
              {Lang.msg.report.availableFor}:
              <UserStickerState
                users={availableTo}
                onChoose={choose}
                options={managers}
                title={Lang.msg.usersStr}
                withButton={true}
              >
                <div className={bem('edit-users')}>
                  {availableTo.map(uid => (
                    <UserAvatar key={uid} userId={uid} size={24} />
                  ))}
                </div>
              </UserStickerState>
              <HelpHint>{Lang.msg.report.availableForHelp}</HelpHint>
            </div>
          </div>
        )}
        {isStatReport && (
          <div className={bem('section')}>
            <div className={bem('sub-header', {sendmail: true})}>
              {Lang.msg.report.sendToMail}
            </div>
            <Schedule
              schedule={identitySchedule(currentReport.schedule)}
              update={updateSchedule}
              valid={scheduleValid}
              isEmailValid={isEmailValid}
            />
          </div>
        )}
      </WithPerfectScrollbar>
    </Popup>
  )
}

const handlers: StateUpdaters<StatsReportsEditProps, State, Handlers> = {
  changeReport: () => newReport => {
    return {currentReport: newReport}
  }
}

export const StatsReportsEdit = React.memo((props: StatsReportsEditProps) => {
  if (!props.initialReport) {
    return <span />
  }
  const Base = withStateHandlers(
    {currentReport: props.initialReport},
    handlers
  )(StatsReportsEditBase)
  return <Base {...props} />
})
